import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { createProductApi } from "Api/product";
import {
  checkStatusCodeSuccess,
  deleteUploadedFile,
  errorHandler,
  mapToDropdownOptions,
} from "Components/constants/Common";
import {
  validationMessages,
  InputPlaceHolder,
  SelectPlaceHolder,
  negDigitRegex,
} from "Components/constants/validation";
import {
  ProductLabel,
  ConditionOptions,
  RakeNoOptions,
  UomValueOptions,
} from "Components/constants/Product";
import BaseModal from "Components/Base/BaseModal";
import BaseInput from "Components/Base/BaseInput";
import { cancelLabel, submitLabel } from "Components/constants/Role";
import { BaseImageURL } from "Api/services";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  manufacturerListApi,
  materialGroupListApi,
  materialListApi,
  fileUpload,
  listOfUomForDropdown,
} from "Api/common";
import onErrorImg from "../../../assets/images/onError.jpg";
import { QuotationLabel } from "Components/constants/Quotation";

interface ProductModalProps {
  open: boolean;
  toggle: () => void;
  onSuccess: (selectedProduct?: any) => void;
}

const basicDetailsSchema = yup.object({
  productDescription: yup
    .string()
    .required(validationMessages.required(ProductLabel.ProductDescription)),
  materialType: yup
    .string()
    .required(validationMessages.required(ProductLabel.MaterialType)),
  materialGroup: yup
    .string()
    .required(validationMessages.required(ProductLabel.MaterialGroup)),
  uom: yup.string().required(validationMessages.required(ProductLabel.UOM)),
});

const additionalDetailsSchema = yup.object({});

const imageSchema = yup.object({});

const ProductModal: React.FC<ProductModalProps> = ({
  open,
  toggle,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [modalLoader, setModalLoader] = useState(false);
  const [materialList, setMaterialList] = useState<any[]>([]);
  const [materialGroupList, setMaterialGroupList] = useState<any[]>([]);
  const [manufacturerList, setManufacturerList] = useState<any[]>([]);
  const [uomList, setUomList] = useState<any[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [productImages, setProductImages] = useState<any[]>([]);

  const fetchData = () => {
    setLoader(true);
    materialListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setMaterialList(mapToDropdownOptions(resp?.data, "id", "name"));
        }
      })
      .catch((err) => {
        setMaterialList([]);
      })
      .finally(() => {
        setLoader(false);
      });
    materialGroupListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setMaterialGroupList(mapToDropdownOptions(resp?.data, "id", "name"));
        }
      })
      .catch((err) => {
        setMaterialGroupList([]);
      })
      .finally(() => {
        setLoader(false);
      });
    manufacturerListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setManufacturerList(mapToDropdownOptions(resp?.data, "id", "name"));
        }
      })
      .catch((err) => {
        setManufacturerList([]);
      })
      .finally(() => {
        setLoader(false);
      });
    listOfUomForDropdown()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setUomList(mapToDropdownOptions(resp?.data, "id", "uom"));
        } else {
          setUomList(UomValueOptions);
        }
      })
      .catch((err) => {
        setUomList(UomValueOptions);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const handleFileUpload = (files: File[]) => {
    setLoader(true);
    const payload = new FormData();
    files.forEach((file) => {
      payload.append("files", file);
    });

    fileUpload(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.StatusCodes)) {
          const newImages = resp?.data.map((file: string) => ({
            product_image: file,
          }));
          setProductImages((prev) => [...prev, ...newImages]);
          productForm.setFieldValue("productImage", [
            ...productImages,
            ...newImages,
          ]);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
    },
    onDrop: (acceptedFiles) => {
      handleFileUpload(acceptedFiles);
    },
  });

  const handleDeleteImage = (index: number, imageUrl: string) => {
    setProductImages((prev) => prev.filter((_, i) => i !== index));
    productForm.setFieldValue(
      "productImage",
      productImages.filter((_, i) => i !== index)
    );

    if (imageUrl) {
      deleteUploadedFile(imageUrl);
    }
  };

  const getValidationSchema = () => {
    switch (activeStep) {
      case 0:
        return basicDetailsSchema;
      case 1:
        return additionalDetailsSchema;
      case 2:
        return imageSchema;
      default:
        return yup.object();
    }
  };

  const productForm = useFormik({
    initialValues: {
      productName: "",
      productCode: "",
      productDescription: "",
      materialType: "",
      materialGroup: "",
      materialGroupDescription: "",
      uom: "",
      quantity: "",
      minimumQuantity: "",
      rate: "",
      amount: "",
      manufacturer: "",
      condition: "",
      rakeNo: "",
      specifications: "",
      remark: "",
      productImage: [],
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setModalLoader(true);
      const payload = {
        product_name: values.productName,
        product_code: values.productCode,
        product_description: values.productDescription,
        material_id: values.materialType,
        material_group_id: values.materialGroup,
        material_group_description: values.materialGroupDescription,
        manufacturer_id: values.manufacturer || null,
        product_status: values.condition ? values.condition : null,
        rate: Number(values.rate) || null,
        amount: Number(values.amount),
        uom_id: values.uom,
        rake_no: values.rakeNo,
        specifications: values.specifications,
        remark: values.remark,
        productImage: values.productImage.map((img: any) => ({
          product_image: img.product_image,
        })),
      };
      createProductApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            onSuccess({
              id: res?.data?.id,
            });
            toggle();
            productForm.resetForm();
            setActiveStep(0);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          errorHandler(error);
        })
        .finally(() => setModalLoader(false));
    },
  });

  const handleTabClick = async (step: number) => {
    if (step > activeStep) {
      const errors = await productForm.validateForm();
      if (Object.keys(errors).length === 0) {
        setActiveStep(step);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as Record<string, boolean>);
        productForm.setTouched(touchedFields);
      }
    } else {
      setActiveStep(step);
    }
  };

  const handleSubmit = () => {
    productForm.handleSubmit();
  };

  return (
    <BaseModal
      open={open}
      toggle={() => {
        toggle();
        productForm.resetForm();
        setActiveStep(0);
      }}
      submit={() => productForm.handleSubmit()}
      title={QuotationLabel.AddProduct}
      closeLabel={cancelLabel}
      submitLabel={submitLabel}
      closeLabelColor="danger"
      submitLabelColor="success"
      loader={modalLoader}
      size="lg"
    >
      <div className="form-group">
        <div className="card-header-tabs">
          <div className="card-body bg-white">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeStep === 0 ? "active" : ""}`}
                  onClick={() => handleTabClick(0)}
                  type="button"
                >
                  {QuotationLabel.BasicDetails}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeStep === 1 ? "active" : ""}`}
                  onClick={() => handleTabClick(1)}
                  type="button"
                >
                  {QuotationLabel.AdditionalDetails}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeStep === 2 ? "active" : ""}`}
                  onClick={() => handleTabClick(2)}
                  type="button"
                >
                  {QuotationLabel.ProductImage}
                </button>
              </li>
            </ul>
            <div className="tab-content text-muted">
              {activeStep === 0 && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseInput
                          type="textarea"
                          name="productDescription"
                          label={ProductLabel.ProductDescription}
                          placeholder={InputPlaceHolder(
                            ProductLabel.ProductDescription
                          )}
                          value={productForm.values.productDescription}
                          onChange={productForm.handleChange}
                          handleBlur={productForm.handleBlur}
                          touched={productForm.touched.productDescription}
                          error={productForm.errors.productDescription}
                          required
                          rows={1}
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseSelect
                          name="materialType"
                          label={ProductLabel.MaterialType}
                          options={materialList}
                          placeholder={SelectPlaceHolder(
                            ProductLabel.MaterialType
                          )}
                          value={productForm.values.materialType}
                          handleChange={productForm.setFieldValue}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.materialType}
                          touched={productForm.touched.materialType}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseSelect
                          name="materialGroup"
                          label={ProductLabel.MaterialGroup}
                          options={materialGroupList}
                          placeholder={SelectPlaceHolder(
                            ProductLabel.MaterialGroup
                          )}
                          value={productForm.values.materialGroup}
                          handleChange={productForm.setFieldValue}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.materialGroup}
                          touched={productForm.touched.materialGroup}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseSelect
                          name="uom"
                          label={ProductLabel.UOM}
                          options={uomList}
                          placeholder={SelectPlaceHolder(ProductLabel.UOM)}
                          value={productForm.values.uom}
                          handleChange={productForm.setFieldValue}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.uom}
                          touched={productForm.touched.uom}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseSelect
                          name="manufacturer"
                          label={ProductLabel.Make}
                          options={manufacturerList}
                          placeholder={SelectPlaceHolder(ProductLabel.Make)}
                          value={productForm.values.manufacturer}
                          handleChange={productForm.setFieldValue}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.manufacturer}
                          touched={productForm.touched.manufacturer}
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseInput
                          type="text"
                          name="quantity"
                          label={ProductLabel.Quantity}
                          placeholder={InputPlaceHolder(ProductLabel.Quantity)}
                          value={productForm.values.quantity}
                          onChange={(e) => {
                            if (negDigitRegex.test(e.target.value)) {
                              productForm.setFieldValue(
                                "quantity",
                                e.target.value
                              );
                              productForm.setFieldValue(
                                "amount",
                                Number(e.target.value) *
                                  Number(productForm.values.rate)
                              );
                            }
                          }}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.quantity}
                          touched={productForm.touched.quantity}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <BaseInput
                          type="text"
                          name="minimumQuantity"
                          label={ProductLabel.MinimumQuantity}
                          placeholder={InputPlaceHolder(
                            ProductLabel.MinimumQuantity
                          )}
                          value={productForm.values.minimumQuantity}
                          onChange={(e) => {
                            if (negDigitRegex.test(e.target.value)) {
                              productForm.handleChange(e);
                            }
                          }}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.minimumQuantity}
                          touched={productForm.touched.minimumQuantity}
                        />
                      </div>
                      <div className="col-md-4">
                        <BaseInput
                          name="rate"
                          type="number"
                          label={ProductLabel.Rate}
                          placeholder={InputPlaceHolder(ProductLabel.Rate)}
                          value={productForm.values.rate}
                          onChange={(e) => {
                            productForm.setFieldValue("rate", e.target.value);
                            productForm.setFieldValue(
                              "amount",
                              Number(e.target.value) *
                                Number(productForm.values.quantity)
                            );
                          }}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.rate}
                          touched={productForm.touched.rate}
                        />
                      </div>
                      <div className="col-md-4">
                        <BaseInput
                          name="amount"
                          type="number"
                          label={ProductLabel.Amount}
                          placeholder={InputPlaceHolder(ProductLabel.Amount)}
                          value={productForm.values.amount}
                          onChange={productForm.handleChange}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.amount}
                          touched={productForm.touched.amount}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeStep === 1 && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseInput
                          name="materialGroupDescription"
                          type="textarea"
                          label={ProductLabel.MaterialGroupDescription}
                          placeholder={InputPlaceHolder(
                            ProductLabel.MaterialGroupDescription
                          )}
                          value={productForm.values.materialGroupDescription}
                          onChange={productForm.handleChange}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.materialGroupDescription}
                          touched={productForm.touched.materialGroupDescription}
                          rows={1}
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseSelect
                          name="condition"
                          label={ProductLabel.Condition}
                          options={ConditionOptions}
                          placeholder={SelectPlaceHolder(
                            ProductLabel.Condition
                          )}
                          value={productForm.values.condition}
                          handleChange={productForm.setFieldValue}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.condition}
                          touched={productForm.touched.condition}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseSelect
                          name="rakeNo"
                          label={ProductLabel.RakeNo}
                          options={RakeNoOptions}
                          placeholder={SelectPlaceHolder(ProductLabel.RakeNo)}
                          value={productForm.values.rakeNo}
                          handleChange={productForm.setFieldValue}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.rakeNo}
                          touched={productForm.touched.rakeNo}
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseInput
                          name="specifications"
                          type="textarea"
                          label={ProductLabel.Specifications}
                          placeholder={InputPlaceHolder(
                            ProductLabel.Specifications
                          )}
                          value={productForm.values.specifications}
                          onChange={productForm.handleChange}
                          handleBlur={productForm.handleBlur}
                          error={productForm.errors.specifications}
                          touched={productForm.touched.specifications}
                          rows={1}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <BaseInput
                      name="remark"
                      type="textarea"
                      label={ProductLabel.Remark}
                      placeholder={InputPlaceHolder(ProductLabel.Remark)}
                      value={productForm.values.remark}
                      onChange={productForm.handleChange}
                      handleBlur={productForm.handleBlur}
                      error={productForm.errors.remark}
                      touched={productForm.touched.remark}
                      rows={1}
                    />
                  </div>
                </div>
              )}
              {activeStep === 2 && (
                <div className="row g-3">
                  <div className="col-12">
                    <label id="productId" className="form-label">
                      {ProductLabel.Images}
                    </label>
                    <div>
                      <div
                        {...getRootProps()}
                        className="border border-gray-900 custom-dropzone rounded-3 p-4 text-center d-flex align-items-center justify-content-center dz-clickable"
                      >
                        <input {...getInputProps()} />
                        <p>{ProductLabel.DragAndDrop}</p>
                      </div>
                      <div className="row mt-3">
                        {productImages.length <= 4 ? (
                          productImages.map((image: any, index: number) => (
                            <div className="col-6 col-md-3" key={index}>
                              <div className="card position-relative">
                                <img
                                  src={`${BaseImageURL}${image?.product_image}`}
                                  alt={`img-${index}`}
                                  className="img-fluid img-card"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;
                                    target.src = onErrorImg;
                                  }}
                                />
                                <i
                                  className="position-absolute mdi mdi-close fs-4 text-dark cursor-pointer top-0 end-0 img-icon"
                                  onClick={() =>
                                    handleDeleteImage(
                                      index,
                                      image?.product_image
                                    )
                                  }
                                ></i>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-12">
                            <div className="d-flex gap-2 overflow-auto">
                              {productImages.map(
                                (image: any, index: number) => (
                                  <div className="card" key={index}>
                                    <img
                                      src={`${BaseImageURL}${image?.product_image}`}
                                      alt={`img-${index}`}
                                      className="img-fluid img-container"
                                      onError={(e) => {
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.onerror = null;
                                        target.src = onErrorImg;
                                      }}
                                    />
                                    <i
                                      className="position-absolute mdi mdi-close fs-4 text-dark cursor-pointer top-0 end-0 img-icon"
                                      onClick={() =>
                                        handleDeleteImage(
                                          index,
                                          image?.product_image
                                        )
                                      }
                                    ></i>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default ProductModal;
